import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/useAuth";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { useAutoUpdater } from "@/hooks/useAutoUpdater";
import { ThemeProvider } from "@/contexts/ThemeContext";
const Landing = lazy(() => import("./pages/Landing"));
const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const AppsDashboard = lazy(() => import("./pages/AppsDashboard"));
const AppWrapper = lazy(() => import("./pages/AppWrapper"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Inventory = lazy(() => import("./pages/Inventory"));
const Auctions = lazy(() => import("./pages/Auctions"));
const Sellers = lazy(() => import("./pages/Sellers"));
const Settings = lazy(() => import("./pages/Settings"));
const Profile = lazy(() => import("./pages/Profile"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Subtle fallback for in-flight lazy chunks. The full-screen
// spinner used to flash for ~200ms on every navigation because the
// previous page unmounts before the new chunk finishes loading;
// this version is just a tiny accent stripe at the top of the
// viewport so the page background stays in place and the
// transition feels seamless. Pair with React Router's
// `v7_startTransition` future flag to keep the old page rendered
// while the new chunk loads in the background.
const RouteFallback = () => (
  <div className="fixed inset-x-0 top-0 z-50 h-0.5 overflow-hidden">
    <div className="h-full w-1/3 bg-primary animate-[loading_1.2s_ease-in-out_infinite]" />
  </div>
);

// Tuned defaults: stop refetching every focus (was a major source of latency
// in the old MVP), keep results fresh for 5 minutes, hold cached data 30 min
// before garbage collection, and retry once with a backoff on failures.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60_000,
      gcTime: 30 * 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    },
    mutations: {
      retry: 0,
    },
  },
});

// Mounted inside BrowserRouter so useNavigate() resolves. Keyboard
// shortcuts work everywhere (login, dashboard, dialogs); pages opt-in
// to specific events via onShortcut('save', ...).
function GlobalShortcuts({ children }: { children: React.ReactNode }) {
  useKeyboardShortcuts();
  // Auto-poll the updater plugin ~3s after mount on desktop. No-op on
  // web. Without this nothing ever calls check() and users never see
  // update prompts even when GitHub Releases has a new latest.json.
  useAutoUpdater();
  return <>{children}</>;
}

const App = () => (
  <ErrorBoundary scope="app">
  <ThemeProvider>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter
        // React Router future flags — opt into the v7 behavior where
        // navigation state updates run inside React.startTransition.
        // That keeps the previous page visible while the next lazy
        // chunk loads, fixing the white-flash users were seeing.
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <GlobalShortcuts>
        <AuthProvider>
          <Suspense fallback={<RouteFallback />}>
          <ErrorBoundary scope="route">
          <Routes>
            {/* Public marketing landing page. On desktop (Tauri) the
                Landing component immediately redirects to /apps. */}
            <Route path="/" element={<Landing />} />

            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            
            {/* Profile page (outside of org context) */}
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
            
            {/* Apps dashboard (choose workspace) */}
            <Route
              path="/apps"
              element={
                <ProtectedRoute>
                  <AppsDashboard />
                </ProtectedRoute>
              }
            />
            
            {/* Multi-tenant app routes */}
            <Route
              path="/app/:slug"
              element={
                <ProtectedRoute>
                  <AppWrapper />
                </ProtectedRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="inventory" element={<Inventory />} />
              <Route path="auctions" element={<Auctions />} />
              <Route path="sellers" element={<Sellers />} />
              <Route path="settings" element={<Settings />} />
            </Route>
            
            {/* 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </ErrorBoundary>
          </Suspense>
        </AuthProvider>
        </GlobalShortcuts>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </ThemeProvider>
  </ErrorBoundary>
);

export default App;
